// src/pages/admin/AddTestimonial.jsx
import React, { useState } from "react";
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Snackbar,
  Alert,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  LinearProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useNavigate } from "react-router-dom";
import { createTestimonial } from "../../services/api";

/**
 * AddTestimonial.jsx
 *
 * Simple admin page to add a testimonial (name + review).
 * - POST /rockstar/admin/testimonial
 * - Fields: name (required), review (required), isActive (optional)
 *
 * Make sure you export createTestimonial in src/services/api.js:
 * export const createTestimonial = (data) => api.post('/rockstar/admin/testimonial', data);
 */

export default function AddTestimonial() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [review, setReview] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "info", message: "" });

  const resetForm = () => {
    setName("");
    setReview("");
    setIsActive(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setSnack({ open: true, severity: "error", message: "Name is required" });
      return;
    }
    if (!review.trim()) {
      setSnack({ open: true, severity: "error", message: "Review is required" });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: String(name).trim(),
        review: String(review).trim(),
        isActive: Boolean(isActive),
      };

      const res = await createTestimonial(payload);
      setSnack({ open: true, severity: "success", message: "Testimonial added successfully" });
      resetForm();

      // optionally navigate to testimonials list after a short delay
      // navigate('/website-settings/testimonials');
    } catch (err) {
      console.error("createTestimonial err", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to add testimonial";
      setSnack({ open: true, severity: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4, borderRadius: 3, boxShadow: "0 8px 30px rgba(0,0,0,0.12)" }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Add Testimonial
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Add a new testimonial that will appear on the website. Only name and review are required.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={6} display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={loading}
                />
              }
              label={isActive ? "Active" : "Inactive"}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Review"
              value={review}
              onChange={(e) => setReview(e.target.value)}
              fullWidth
              multiline
              rows={6}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sx={{ textAlign: "right" }}>
            <Box display="inline-flex" gap={2}>
              <Button
                variant="outlined"
                onClick={() => {
                  resetForm();
                }}
                disabled={loading}
              >
                Reset
              </Button>

              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={loading}
              >
                Save Testimonial
              </Button>

              <Button
                variant="text"
                onClick={() => navigate("/website-settings/testimonials")}
                disabled={loading}
              >
                View Testimonials
              </Button>
            </Box>
          </Grid>
        </Grid>

        {loading && <LinearProgress sx={{ mt: 2 }} />}

        <Snackbar
          open={snack.open}
          autoHideDuration={3500}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            severity={snack.severity}
            sx={{ width: "100%" }}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
}
